import { Title } from '@gerdau/hefesto/Title';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { TFabricationView } from '..';
import GoBackButtonFacelift from '../../../../components/GoBackButton/Facelift';
import { FabricationPurchaseOrderService } from '../../../../services/FabricationPurchaseOrder';
import { ApplicationState } from '../../../../store';
import { loadContractDetailsV2 } from '../../../../store/ducks/fabrication-contracts/actions';
import { IFabricationContract } from '../../../../store/ducks/fabrication-contracts/types';
import { IFabricationOrder } from '../../../../store/ducks/fabrication-orders/types';
import { sendTagging } from '../../../../store/ducks/tagging/actions';
import { getTaggingInformationScreen } from '../../../../utils/Tagging';
import AnnealedWireContent from './AnnealedWireContent';
import ConstructionAddressContent from './ConstructionAddressContent';
import DetailingProjectContent from './DetailingProjectContent';
import ExtraItensContent from './ExtraItensContent';
import FabricationOrderDetailsActionsButtons from './FabricationOrderDetailsActionsButtons';
import GeneralInfoContent from './GeneralInfoContent';
import OrderDetails from './OrderDetailContent';
import {
  Container,
  ContainerTitle,
  FaceliftContainer,
  SubTitle,
  WhiteBlockContainer
} from './styles';
import { addToast } from '../../../../store/ducks/toast/actions';
import { EToastTypes } from '../../../../store/ducks/toast/types';
import { MarketingCloudService } from '../../../../services/MarketingCloud';

interface IFabricationOrderDetailsProps {
  fabricationOrder: IFabricationOrder;
  onBuyAgainClick: (fabricationOrder: IFabricationOrder) => void;
  goToPurchaseFabrication(): void;
  setActiveView: (activeView: TFabricationView) => void;
}

const FabricationOrderDetails: React.FC<IFabricationOrderDetailsProps> = ({
  fabricationOrder,
  onBuyAgainClick,
  goToPurchaseFabrication,
  setActiveView
}) => {
  const dispatch = useDispatch();

  const contractsState = useSelector(
    (appState: ApplicationState) => appState.fabricationContracts
  );

  const [selectedContract, setSelectedContract] = useState<
    IFabricationContract | undefined
  >(undefined);
  const [loadingPDF, setLoadingPDF] = useState(false);

  const onClickDownloadPDF = () => {
    const { id: createdOrderId, contractNumber } = fabricationOrder;

    if (!createdOrderId) {
      return;
    }

    setLoadingPDF(true);

    FabricationPurchaseOrderService.downloadPurchaseOrderPDF(createdOrderId)
      .then((fabricationPurchaseOrderBase64) => {
        const pdfFileName = `Pedido_${contractNumber}_${createdOrderId}`;

        const dataurl =
          'data:text/plain;base64,' + fabricationPurchaseOrderBase64;
        const element = document.createElement('a');
        element.setAttribute('href', dataurl);
        element.setAttribute('download', `${pdfFileName}.pdf`);
        element.style.display = 'none';
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
      })
      .catch((err) => {
        console.error(err);
        dispatch(
          addToast({
            type: EToastTypes.DANGER,
            message: 'Não foi possível baixar o PDF do pedido. Tente novamente.'
          })
        );
      })
      .finally(() => {
        setLoadingPDF(false);
      });
  };

  const initSelectedContractV2 = () => {
    const contract = contractsState.contracts?.find(
      (contr) => contr.contractNumber === fabricationOrder.contractNumber
    ) as IFabricationContract;

    if (
      !contract.shipTo.address &&
      !contract.loadingDetails &&
      !contract.errorDetails
    ) {
      dispatch(loadContractDetailsV2(contract.contractNumber));
    } else {
      if (!contract.loadingDetails && !contract.errorDetails) {
        setSelectedContract(contract);
      }
    }
  };

  useEffect(() => {
    if (selectedContract === undefined && !contractsState.loading) {
      initSelectedContractV2();
    }
  });

  const { pathname } = useLocation();

  const { screenName = '', itemDescription = '' } = getTaggingInformationScreen(
    pathname,
    true
  );

  useEffect(() => {
    dispatch(
      sendTagging({
        screenName: `${screenName}:DetalhesDoPedido`,
        page: location.pathname,
        event: 'page_view'
      })
    );
  }, []);

  return (
    <Container>
      <WhiteBlockContainer>
        <GoBackButtonFacelift
          title="Voltar"
          onClick={() =>
            setActiveView({ view: 'fabricationTable', data: undefined })
          }
        />
        <ContainerTitle isNewTitleFacelift>
          <Title level={2}>
            {`Detalhes do pedido ${fabricationOrder.orderNumber}`}
          </Title>
          <SubTitle>
            Todas as informações do seu pedido estão aqui. Para informações
            sobre a entrega, retorne para a página anterior.
          </SubTitle>
        </ContainerTitle>
        <FabricationOrderDetailsActionsButtons
          fabricationOrder={fabricationOrder}
          selectedContract={selectedContract}
          onBuyAgainClick={() => {
            dispatch(
              sendTagging({
                screenName: `${screenName}:DetalhesDoPedido`,
                itemName: 'button:recomprar_este_pedido',
                event: 'click',
                itemDescription: `${itemDescription}_detalhesDoPedido_recomprarEstePedido`
              })
            );
            MarketingCloudService.sendEvent({
              type: 'fabrication-orders',
              rebuyOrder: true
            });
            onBuyAgainClick(fabricationOrder);
          }}
          goToPurchaseFabrication={goToPurchaseFabrication}
          onClickDownloadPDF={() => {
            dispatch(
              sendTagging({
                screenName: `${screenName}:DetalhesDoPedido`,
                itemName: 'button:baixar_resumo_do_pedido',
                event: 'click',
                itemDescription: `${itemDescription}_detalhesDoPedido_baixarResumoDoPedido`
              })
            );
            onClickDownloadPDF();
          }}
          loadingPDF={loadingPDF}
          disabled={loadingPDF}
        />
      </WhiteBlockContainer>

      <OrderDetails fabricationOrder={fabricationOrder} />

      <GeneralInfoContent
        fabricationOrder={fabricationOrder}
        contract={selectedContract}
      />
      <ConstructionAddressContent
        loading={!selectedContract || contractsState.loading}
        contract={selectedContract}
      />

      <FaceliftContainer>
        <DetailingProjectContent fabricationOrder={fabricationOrder} />
      </FaceliftContainer>

      {fabricationOrder.otherPieces.length > 0 && (
        <FaceliftContainer>
          <ExtraItensContent extraItens={fabricationOrder.otherPieces} />
        </FaceliftContainer>
      )}

      {fabricationOrder.annealedWires.length > 0 && (
        <FaceliftContainer lastItem>
          <AnnealedWireContent annealedWires={fabricationOrder.annealedWires} />
        </FaceliftContainer>
      )}
    </Container>
  );
};

export default FabricationOrderDetails;
